import React, { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, GlobeAltIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';

const MAX_DOMAINS = 10;

interface AllowedDomainsModalProps {
  isOpen: boolean;
  onClose: () => void;
  domains: string[];
  onAddDomain: (domain: string) => Promise<boolean>;
  onRemoveDomain: (domain: string) => Promise<boolean>;
}

export default function AllowedDomainsModal({
  isOpen,
  onClose,
  domains,
  onAddDomain,
  onRemoveDomain,
}: AllowedDomainsModalProps) {
  const [newDomain, setNewDomain] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [removingDomain, setRemovingDomain] = useState<string | null>(null);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return;

    const domainToAdd = newDomain.trim().toLowerCase();

    // Basic domain validation
    if (!/^[a-z0-9]+([\-\.][a-z0-9]+)*(\:[0-9]+)?$/.test(domainToAdd)) {
      toast.error('Please enter a valid domain (e.g., example.com or localhost:3000)');
      return;
    }

    if (domains.includes(domainToAdd)) {
      toast.error('This domain is already in the list');
      return;
    }

    if (domains.length >= MAX_DOMAINS) {
      toast.error(`Maximum of ${MAX_DOMAINS} domains allowed`);
      return;
    }

    try {
      setIsAdding(true);
      const success = await onAddDomain(domainToAdd);
      if (success) {
        setNewDomain('');
      }
    } finally {
      setIsAdding(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    try {
      setRemovingDomain(domain);
      await onRemoveDomain(domain);
    } finally {
      setRemovingDomain(null);
    }
  };

  const handleClose = () => {
    setNewDomain('');
    onClose();
  };

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100"
                  >
                    Allowed Domains
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    onClick={handleClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Only these domains can use your chatbot embed. The first domain is automatically added when someone uses your embed code.
                  </p>
                </div>

                {/* Domain list - line by line like Discord integrations */}
                {domains.length > 0 && (
                  <div className="mb-4 space-y-2">
                    {domains.map((domain) => (
                      <div
                        key={domain}
                        className="flex items-center justify-between p-2 bg-gray-50 dark:bg-slate-800 border border-gray-300 dark:border-slate-600 rounded-md"
                      >
                        <div className="flex items-center text-sm">
                          <GlobeAltIcon className="h-4 w-4 text-primary-600 dark:text-primary-400 mr-2" />
                          <span className="text-gray-900 dark:text-gray-100 font-mono">
                            {domain}
                          </span>
                        </div>
                        <button
                          onClick={() => handleRemoveDomain(domain)}
                          disabled={removingDomain === domain}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm disabled:opacity-50"
                          title="Remove domain"
                        >
                          {removingDomain === domain ? (
                            <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-current"></div>
                          ) : (
                            '×'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add domain form */}
                <div className="flex items-center space-x-2 mb-4">
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDomain())}
                    placeholder="example.com"
                    disabled={domains.length >= MAX_DOMAINS}
                    className="flex-1 text-sm bg-white dark:bg-slate-900 px-3 py-2 rounded-lg border border-gray-200 dark:border-slate-500 font-mono text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-600 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  <button
                    onClick={handleAddDomain}
                    disabled={!newDomain.trim() || isAdding || domains.length >= MAX_DOMAINS}
                    className="px-4 py-2 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-600"
                  >
                    {isAdding ? (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1"></div>
                      </div>
                    ) : (
                      'Add'
                    )}
                  </button>
                </div>

                {/* Domain count indicator */}
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {domains.length} of {MAX_DOMAINS} domains used
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
